import { Leaf, Wifi, Plug, Armchair } from "lucide-react";
import type { PlanFlightAmenities } from "@/lib/types";

/* Neutral, never-alarmist emissions badge + amenities row. Both render ONLY
   when the backend supplies the data; nothing is fabricated or estimated here. */

interface FlightExtrasProps {
  emissionsKgCo2e?: number | null;
  amenities?: PlanFlightAmenities | null;
}

function hasAmenities(a: PlanFlightAmenities | null | undefined): boolean {
  if (!a) return false;
  return Boolean(a.wifi) || Boolean(a.power) || Boolean((a.legroom || "").trim());
}

export default function FlightExtras({ emissionsKgCo2e, amenities }: FlightExtrasProps) {
  const showEmissions =
    emissionsKgCo2e != null && Number.isFinite(emissionsKgCo2e) && emissionsKgCo2e >= 0;
  const showAmenities = hasAmenities(amenities);
  const legroom = (amenities?.legroom || "").trim();

  if (!showEmissions && !showAmenities) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 border-t pt-3 text-xs">
      {showEmissions && (
        <span
          className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 font-medium text-muted-foreground"
          title="Estimated carbon emissions"
        >
          <Leaf className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span className="font-mono tabular-nums">
            ~{Math.round(emissionsKgCo2e as number).toLocaleString()} kg CO₂e
          </span>
        </span>
      )}

      {showAmenities && (
        <span className="inline-flex items-center gap-2 text-muted-foreground">
          {amenities?.wifi && (
            <span className="inline-flex items-center gap-1" title="Wi-Fi available">
              <Wifi className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span className="sr-only">Wi-Fi</span>
            </span>
          )}
          {amenities?.power && (
            <span className="inline-flex items-center gap-1" title="In-seat power">
              <Plug className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span className="sr-only">In-seat power</span>
            </span>
          )}
          {legroom && (
            <span className="inline-flex items-center gap-1" title="Legroom">
              <Armchair className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span>{legroom}</span>
            </span>
          )}
        </span>
      )}
    </div>
  );
}
